import { useQueryParams } from '@calendly/hooks'
import { useSelector } from 'react-redux'
import detect from 'detect.js'
import { useCallback, useEffect, useRef } from 'react'

import { countryCodeSelector } from 'selectors'
import { isCookiesAllowedSelector } from 'selectors/gdpr'
import { eventService } from 'services/analytics/event_service'
import { currentUserSelector } from 'selectors/user'
import { isEmbeddedSelector } from 'selectors/embed'
import { bookingLinkService } from 'services/analytics/booking_link_service'
import { customDomainSelector } from 'selectors/custom_domain'

const sha256 = async (value) => {
  if (typeof window.crypto.subtle === 'undefined') { return Promise.resolve('') }
  const msgUint8 = new TextEncoder().encode(value)
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export const useSendSegmentEvent = () => {
  const { embed_type: embedType, utm_content: utmContent, utm_source: utmSource, preview_source: previewSource } = useQueryParams()
  const timerRef = useRef(null)
  const hasDispatched = useRef(false)
  const lastBookingPageTypeRef = useRef(null)
  const allowed = useSelector(isCookiesAllowedSelector('performance')) && embedType !== 'preview'
  const currentUser = useSelector(currentUserSelector)
  const countryCode = useSelector(countryCodeSelector)
  const isEmbedded = useSelector(isEmbeddedSelector)
  const ua = detect.parse(navigator.userAgent)
  const utmContentValue = typeof utmContent === 'string' ? utmContent : null
  const utmSourceValue = typeof utmSource === 'string' ? utmSource : null
  const bookingLinkHost = window.location.origin
  const { isCustomDomain, linkDomain } = bookingLinkService.linkDomainData(
    bookingLinkHost,
    useSelector(customDomainSelector).default_domain
  )

  const track = useCallback((ownerType, owningUser, ownerId, bookingPageType, code) => {
    if (allowed && window.analytics && (!hasDispatched || lastBookingPageTypeRef.current !== bookingPageType)) {
      const hasAnonymousId = typeof (window.analytics.user) === 'function' && typeof (window.analytics.user().anonymousId) === 'function'
      hasDispatched.current = true
      lastBookingPageTypeRef.current = bookingPageType

      sha256(window.location.href).then((hash) => eventService.dispatch({
        url: '/api/booking/analytics/track',
        name: 'Booking Page Viewed',
        version: 1,
        dependencies: { user_id: owningUser.id },
        entities: ['user', 'organization'],
        properties: {
          is_owner_session: currentUser?.uuid === owningUser.uuid,
          is_calendly_user: !!(currentUser?.uuid),
          is_embedded: isEmbedded,
          is_preview: !!previewSource,
          preview_source: previewSource,
          invitee_calendly_user_id: undefined,
          invitee_calendly_user_uuid: currentUser?.uuid,
          invitee_country_code: countryCode,
          invitee_browser: ua.browser.name,
          invitee_device: `${ua.device.name} ${ua.os.name}`,
          booking_page_type: bookingPageType,
          owner_type: ownerType,
          owner_id: ownerId,
          invitee_anonymous_id: hasAnonymousId ? window.analytics.user().anonymousId() : '',
          utm_content: utmContentValue,
          utm_source: utmSourceValue,
          custom_domain: isCustomDomain,
          domain: linkDomain,
          url_hashed: hash,
          is_disabled: ['event_type_deactivated', 'event_type_deleted'].includes(code)
        }
      })
      )
    }
  }, [
    allowed,
    countryCode,
    currentUser?.uuid,
    isCustomDomain,
    isEmbedded,
    linkDomain,
    previewSource,
    ua.browser.name,
    ua.device.name,
    ua.os.name,
    utmContentValue,
    utmSourceValue
  ])

  // Cleanup any pending debounce on unmount
  useEffect(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  /**
   * Returned debounced sender
   * - Starts/restarts a 500ms timer; on fire, calls track().
   * - Useful when route or component redraws happen in quick succession.
   */

  return useCallback((ownerType, owningUser, ownerId, bookingPageType, code) => {
    // Reset existing timer before starting a new one
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null
      track(ownerType, owningUser, ownerId, bookingPageType, code)
    }, 500)
  }, [track])
}
